import { AlertTriangle, CheckCircle2, Info, Clock, Upload } from "lucide-react";

const ICONS = {
    error: AlertTriangle,
    warning: AlertTriangle,
    info: Info,
};

const SEV_COLOR = {
    error: "var(--brand-red)",
    warning: "var(--brand-warning)",
    info: "var(--brand-text-muted)",
};

/** Read-only banner shown at the top of pre-estimates generated from bid intake.
 * Receives the raw gap_report payload from the API. */
export default function GapReportBanner({ gapReport, jobId }) {
    if (!gapReport) return null;
    const extracted = gapReport.extracted || [];
    const notExtracted = gapReport.not_extracted || [];
    const issues = gapReport.issues_found || [];
    const missing = gapReport.missing_documents || [];

    return (
        <div
            className="brand-card fade-up"
            style={{
                background:
                    "linear-gradient(180deg, rgba(237,108,2,0.06) 0%, rgba(237,108,2,0.02) 100%)",
                borderColor: "rgba(237,108,2,0.35)",
            }}
            data-testid="gap-report-banner"
        >
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="eyebrow" style={{ color: "var(--brand-warning)" }}>
                        <Upload size={12} className="inline mr-1" />
                        Generated from bid intake
                    </p>
                    <h3 className="h3 mt-2">
                        Paint systems extracted — enter quantities manually.
                    </h3>
                    <p className="text-muted text-sm mt-1 flex items-center gap-1">
                        <Clock size={13} />
                        Estimated time to complete: 45–60 minutes.
                    </p>
                </div>
                {jobId && (
                    <a
                        href={`/admin/bid-intake/${jobId}`}
                        className="link-red text-sm"
                        data-testid="gap-banner-job-link"
                    >
                        View intake job →
                    </a>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <div>
                    <p className="eyebrow" style={{ color: "var(--brand-success)" }}>
                        Extracted
                    </p>
                    <ul className="mt-2 space-y-1.5 text-sm">
                        {extracted.length === 0 && (
                            <li className="text-muted">Nothing extracted.</li>
                        )}
                        {extracted.map((s, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2"
                                data-testid="gap-extracted-row"
                            >
                                <CheckCircle2
                                    size={14}
                                    className="mt-0.5 shrink-0"
                                    style={{ color: "var(--brand-success)" }}
                                />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="eyebrow">Not extracted</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-muted">
                        {notExtracted.map((s, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2"
                                data-testid="gap-not-extracted-row"
                            >
                                <span className="mt-0.5">·</span>
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {(issues.length > 0 || missing.length > 0) && (
                <div className="mt-5">
                    <p className="eyebrow">Heads-up</p>
                    <ul className="mt-2 space-y-1.5 text-sm">
                        {issues.map((it, i) => {
                            const Icon = ICONS[it.severity] || Info;
                            return (
                                <li
                                    key={`is-${i}`}
                                    className="flex items-start gap-2"
                                    data-testid={`gap-issue-${it.severity}`}
                                >
                                    <Icon
                                        size={14}
                                        className="mt-0.5 shrink-0"
                                        style={{ color: SEV_COLOR[it.severity] }}
                                    />
                                    <span>{it.message}</span>
                                </li>
                            );
                        })}
                        {missing.map((m, i) => (
                            <li
                                key={`mi-${i}`}
                                className="flex items-start gap-2"
                                data-testid="gap-missing-row"
                            >
                                <AlertTriangle
                                    size={14}
                                    className="mt-0.5 shrink-0"
                                    style={{ color: "var(--brand-red)" }}
                                />
                                <span>Missing: {m}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
